// The LIVE-SPEECH drive weights, editable while the character talks.
//
// Sibling of rigConstrainBlend's `rigConstrain`, and a singleton for the same
// reason: OGSModel.driveSplatFace reads this every frame, so it must never move
// into React state (the graphState rule), and an edit has to land on the next
// frame — the question it answers ("how much of this does this character want?")
// is only answerable while watching a face talk.
//
// Today it holds ONE weight, the live mouth mix (`?livemouth`). That mix lerps
// the mouth block toward the interpolation between this bundle's trained
// closed/open references, with the A2F jaw picking the coordinate — so A2F still
// owns the aperture and the references own what a mouth at that aperture looks
// like (see liveRigRegions.js for why rest+delta lands off the manifold).
//
// It is a DRIVE, not a constraint: it rewrites the mouth rather than bounding it,
// which is why it is a separate weight from `rigConstrain` and not blended by it.
// The two interact, and that interaction is the reason this is editable at all —
// at the shipped 0.65 the mix has already pulled the mouth most of the way onto
// the trained manifold before either constraint runs, so it removes most of what
// the constraint would otherwise have to remove there, and the constraint's
// effect on a talking face looks far smaller than on a static extreme pose.

// The DEFAULT is now the shared middle (0.5), the same value the constraint
// blend defaults to, and it is deliberately NOT liveRigRegions'
// DEFAULT_LIVE_MOUTH_WEIGHT (0.65) any more. That constant stays where it is
// and keeps its meaning -- the weight this mix shipped with -- because other
// call sites still describe the shipped face in terms of it; what changed is
// which value an untouched character STARTS at, and that is this module's
// business, not liveRigRegions'.
import { DEFAULT_FACE_RIG_WEIGHT } from './faceRigDefaults.js'

/** The starting weight for a character that has never been dialled in. */
export const DEFAULT_MOUTH_WEIGHT = DEFAULT_FACE_RIG_WEIGHT

/**
 *   mouthWeight  what driveSplatFace applies each frame.
 *   initial      the value the URL/default resolved to — what `reset` restores.
 *   source       'url' | 'panel' | 'default', so the row can say who decided.
 *   available    this bundle HAS a live mouth drive (a `mouth_controller` or the
 *                global macros pair). Without one the mix never runs whatever the
 *                weight says, and a slider that silently does nothing is worse
 *                than no slider.
 */
export const liveFaceControls = {
  mouthWeight: DEFAULT_MOUTH_WEIGHT,
  initial: DEFAULT_MOUTH_WEIGHT,
  source: 'default',
  available: false,
}

const subscribers = new Set()

function notify() {
  for (const fn of subscribers) {
    try { fn(liveFaceControls) } catch (e) { console.error('[ogs] liveFaceControls subscriber threw:', e) }
  }
}

/** Subscribe to changes (UI cadence). Returns an unsubscribe. */
export function subscribeLiveFaceControls(fn) {
  subscribers.add(fn)
  return () => subscribers.delete(fn)
}

const clamp01 = (v) => (Number.isFinite(v) ? (v < 0 ? 0 : v > 1 ? 1 : v) : null)

/**
 * Arm from the URL at pipeline start, once the bundle's mouth drive is known.
 *
 * The URL's one chance to speak: a later edit outranks it, and a character switch
 * re-arms rather than inheriting the previous character's setting — another
 * bundle's references pose different controls.
 */
export function armLiveMouthWeight(value, { available = true } = {}) {
  // `value` is null when ?livemouth was not typed, and the default applies.
  const url = clamp01(value)
  const v = url ?? DEFAULT_MOUTH_WEIGHT
  liveFaceControls.mouthWeight = v
  liveFaceControls.initial = v
  liveFaceControls.source = url != null ? 'url' : 'default'
  liveFaceControls.available = available
  notify()
  return v
}

/** Panel/console edit. Out of range or non-finite is REFUSED, not clamped silently. */
export function setLiveMouthWeight(value, source = 'panel') {
  const v = clamp01(value)
  if (v == null) {
    console.warn(`[ogs] livemouth: ${value} is not a number in [0, 1] — ignored`)
    return liveFaceControls.mouthWeight
  }
  liveFaceControls.mouthWeight = v
  liveFaceControls.source = source
  notify()
  return v
}

/** Teardown: the next character arms its own weight. */
export function resetLiveFaceControls() {
  liveFaceControls.mouthWeight = DEFAULT_MOUTH_WEIGHT
  liveFaceControls.initial = DEFAULT_MOUTH_WEIGHT
  liveFaceControls.source = 'default'
  liveFaceControls.available = false
  notify()
}
